import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";

/**
 * Where the palette marking file puts the palette on the plate.
 *
 * Draws the laser's marking field, the plate of the selection's exploration
 * (at the exploration's plate offset), its usable part and the palette block
 * with its patches (and, for a mode contrast selection, the group frames and
 * texts). The block is dragged with the pointer or typed as a position: its
 * top-left corner in mm from the plate's top-left corner. The geometry comes
 * from the server (POST api/selection/palette/marking/layout), the same
 * computation as the marking file.
 *
 * Props:
 *   selectionId
 *   patchSize                   : mm, or null while the typed size is not a positive number
 *   markCrosses
 *   position                    : { x, y } in mm from the plate's corner, or null for the default
 *   grid                        : the palette's grid [rows, columns] as stored on the server, or
 *                                 null; a change re-requests the layout (the server reads the grid)
 *   onPositionChange(position)
 *   onLayoutChange(layout|null) : the server's layout, null while it could not be computed
 *   colors                      : { [sampleId]: css colour } for the patches
 */
export const LAYOUT_URL = "api/selection/palette/marking/layout";
const CANVAS_WIDTH_PX = 560;
const MARGIN_MM = 8;

const roundMm = (value) => Math.round(value * 10) / 10;
const fmt = (value) => (Number.isFinite(value) ? String(Math.round(value * 10) / 10) : "?");

export default function PalettePlacement({
  selectionId,
  patchSize,
  markCrosses,
  position,
  grid = null,
  onPositionChange,
  onLayoutChange,
  colors = {},
}) {
  const [layout, setLayout] = useState(null);
  const [error, setError] = useState("");
  const dragRef = useRef(null);
  const requestRef = useRef(0);

  // A drag fires many position changes a second; the server is asked once
  // the position rests for a moment (stale answers are dropped by request id).
  useEffect(() => {
    if (!(patchSize > 0)) return undefined;
    const timer = setTimeout(async () => {
      const id = ++requestRef.current;
      try {
        const res = await axios.post(LAYOUT_URL, {
          selectionId,
          patchSize,
          markCrosses,
          ...(position ? { position } : {}),
        });
        if (id !== requestRef.current) return;
        const next = res?.data?.layout || null;
        setLayout(next);
        setError("");
        if (onLayoutChange) onLayoutChange(next);
        if (next && !position) onPositionChange(next.position);
      } catch (err) {
        if (id !== requestRef.current) return;
        setLayout(null);
        setError(err?.response?.data?.error || "The palette position could not be computed.");
        if (onLayoutChange) onLayoutChange(null);
      }
    }, 120);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionId, patchSize, markCrosses, position?.x, position?.y, grid ? grid.join("x") : ""]);

  // The block follows the pointer before the server answers: everything of
  // the palette is drawn shifted by the distance to the position it was
  // computed for.
  const shift = layout && position
    ? { x: position.x - layout.position.x, y: position.y - layout.position.y }
    : { x: 0, y: 0 };

  const world = useMemo(() => {
    if (!layout) return null;
    const { field_mm: field, plate_mm: plate } = layout;
    const minX = Math.min(0, plate.x) - MARGIN_MM;
    const minY = Math.min(0, plate.y) - MARGIN_MM;
    const maxX = Math.max(field.width, plate.x + plate.width) + MARGIN_MM;
    const maxY = Math.max(field.height, plate.y + plate.height) + MARGIN_MM;
    return { minX, minY, width: maxX - minX, height: maxY - minY };
  }, [layout]);

  if (!layout || !world) {
    return error ? <Alert severity="error">{error}</Alert> : null;
  }

  const { field_mm: field, plate_mm: plate, usable_mm: usable, palette_mm: palette } = layout;
  const scale = CANVAS_WIDTH_PX / world.width;
  const canvasHeight = world.height * scale;
  const px = (mm) => mm * scale;
  const toX = (mm) => px(mm - world.minX);
  const toY = (mm) => px(mm - world.minY);
  const bx = (mm) => toX(mm + shift.x);
  const by = (mm) => toY(mm + shift.y);

  // Positions that keep the block on the usable part of the plate.
  const bounds = {
    minX: usable.x - plate.x,
    minY: usable.y - plate.y,
    maxX: Math.max(usable.x - plate.x, usable.x + usable.width - plate.x - palette.width),
    maxY: Math.max(usable.y - plate.y, usable.y + usable.height - plate.y - palette.height),
  };
  const clamp = (value, low, high) => Math.min(Math.max(value, low), high);

  const onPointerDown = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { startX: event.clientX, startY: event.clientY, origin: { ...(position || layout.position) } };
  };
  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const dx = (event.clientX - dragRef.current.startX) / scale;
    const dy = (event.clientY - dragRef.current.startY) / scale;
    onPositionChange({
      x: roundMm(clamp(dragRef.current.origin.x + dx, bounds.minX, bounds.maxX)),
      y: roundMm(clamp(dragRef.current.origin.y + dy, bounds.minY, bounds.maxY)),
    });
  };
  const onPointerUp = (event) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const current = position || layout.position;
  const setAxis = (axis) => (event) => {
    const value = parseFloat(event.target.value);
    if (Number.isFinite(value)) onPositionChange({ ...current, [axis]: value });
  };
  const toCorner = () => onPositionChange({ ...layout.default_position });
  const toCentre = () => onPositionChange({
    x: roundMm(bounds.minX + (usable.width - palette.width) / 2),
    y: roundMm(bounds.minY + (usable.height - palette.height) / 2),
  });

  const fits = layout.fits;
  const textPx = layout.text_height_mm ? px(layout.text_height_mm) : 0;

  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg
          data-testid="palette-placement-canvas"
          width={CANVAS_WIDTH_PX}
          height={canvasHeight}
          viewBox={`0 0 ${CANVAS_WIDTH_PX} ${canvasHeight}`}
          style={{ display: "block", touchAction: "none", userSelect: "none" }}
        >
          {/* marking field */}
          <rect
            x={toX(0)}
            y={toY(0)}
            width={px(field.width)}
            height={px(field.height)}
            fill="#f4f6f8"
            stroke="#5b6b7a"
            strokeWidth={1.5}
            strokeDasharray="6 4"
          />
          <text x={toX(0)} y={toY(field.height) + 14} fontSize={12} fill="#5b6b7a">
            {`Marking field ${fmt(field.width)} × ${fmt(field.height)} mm`}
          </text>
          {/* plate and its usable part */}
          <rect x={toX(usable.x)} y={toY(usable.y)} width={px(usable.width)} height={px(usable.height)} fill="#dbeafe" />
          <rect
            x={toX(plate.x)}
            y={toY(plate.y)}
            width={px(plate.width)}
            height={px(plate.height)}
            fill="none"
            stroke="#4C7EFF"
            strokeWidth={2}
          />
          <text x={toX(plate.x) + 6} y={toY(plate.y) - 5} fontSize={12} fill="#4C7EFF">
            {`Plate ${fmt(plate.width)} × ${fmt(plate.height)} mm`}
          </text>
          {/* palette (draggable) */}
          <g data-testid="palette-block" style={{ cursor: "grab" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <rect
              x={bx(palette.x)}
              y={by(palette.y)}
              width={px(palette.width)}
              height={px(palette.height)}
              fill="rgba(255, 255, 255, 0.85)"
              stroke={fits ? "#1f2937" : "#d32f2f"}
              strokeWidth={fits ? 1 : 2}
            />
            {layout.patches.map((patch, index) => (
              <rect
                key={`${patch.sample_id}-${index}`}
                data-testid="palette-patch"
                x={bx(patch.x)}
                y={by(patch.y)}
                width={px(layout.patch_size)}
                height={px(layout.patch_size)}
                fill={colors[patch.sample_id] || "#94a3b8"}
              />
            ))}
            {layout.frames.map((frame, index) => (
              <rect
                key={`frame-${index}`}
                data-testid="palette-frame"
                x={bx(frame.x)}
                y={by(frame.y)}
                width={px(frame.width)}
                height={px(frame.height)}
                fill="none"
                stroke="#1f2937"
                strokeWidth={0.75}
              />
            ))}
            {layout.texts.map((text, index) => (
              <text
                key={`text-${index}`}
                x={bx(text.x)}
                y={by(text.y) + textPx}
                fontSize={textPx}
                fill="#1f2937"
              >
                {text.text}
              </text>
            ))}
          </g>
          <circle cx={toX(0)} cy={toY(0)} r={3} fill="#5b6b7a" />
        </svg>
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          label="Position x (mm)"
          type="number"
          size="small"
          inputProps={{ step: 0.5 }}
          value={current.x}
          onChange={setAxis("x")}
          sx={{ width: 150 }}
        />
        <TextField
          label="Position y (mm)"
          type="number"
          size="small"
          inputProps={{ step: 0.5 }}
          value={current.y}
          onChange={setAxis("y")}
          sx={{ width: 150 }}
        />
        <Button variant="outlined" size="small" onClick={toCorner} sx={{ textTransform: "none" }}>
          Corner
        </Button>
        <Button variant="outlined" size="small" onClick={toCentre} sx={{ textTransform: "none" }}>
          Centre
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" data-testid="palette-placement-summary">
        {`Palette ${fmt(palette.width)} × ${fmt(palette.height)} mm. Position: its top-left corner from the plate's top-left corner.`}
      </Typography>
      {!fits && layout.error ? <Alert severity="error">{layout.error}</Alert> : null}
    </Stack>
  );
}
